import { useMemo } from 'react';
import { Highlighter } from 'react-bootstrap-typeahead';

import { searchConnections, type ConnectionSummary } from 'api/connections/endpoints/searchConnections';

import { EditReference } from './EditReference';
import type { ReferenceSource } from './referenceSource';

// The minimal identifying shape this control deals in — the same projection the search endpoint
// validates, so a search result passes straight through. Aliased rather than redeclared because the
// two are the same type by identity, not by coincidence.
export type ConnectionReference = ConnectionSummary;

interface Props {
  // The selected connection, or null for "none". A whole reference rather than a bare id, since a
  // caller may need the name as well as the key. A caller holding only the id passes it with an empty
  // `connection_name`, and the control fetches the name.
  readonly value: ConnectionReference | null;
  readonly onChange: (value: ConnectionReference | null) => void;
  /** Floating label. Omit to render a bare control (the caller supplies its own label), as `EditString` does. */
  readonly label?: string;
  /** Text shown while nothing is selected. Say what empty means, rather than restating the label. */
  readonly placeholder?: string;
  readonly mandatory?: boolean;
  readonly disabled?: boolean;
  // Restrict to one connection type ('rest', 'ssh', …; server-side case-insensitive equals).
  // Undefined widens to every type.
  readonly type?: string;
}

const getKey = (c: ConnectionReference) => c.connection_id;

const getLabel = (c: ConnectionReference): string => c.connection_name;

/**
 * Searchable dropdown for connections keyed on `connection_id`. Selection returns the whole reference; `resolve`
 * hydrates a stored id to its name.
 *
 * Backed by server-side search (`GET /connections?search=…`), so the option list is not bounded by what fits in one
 * eager fetch. Two-column row layout (name + type, muted right-aligned as informational context); the server matches
 * on name only. Names are not unique, so an id is the only safe key.
 */
export const EditConnectionReference = ({ value, onChange, label, placeholder, mandatory, disabled, type }: Props) => {
  // Re-created when the scope changes so the model re-fetches; stable otherwise (a fresh closure
  // would rebuild the model every render).
  const source = useMemo<ReferenceSource<ConnectionReference>>(
    () => ({
      kind: 'search',
      search: (query, limit) => searchConnections({ search: query || undefined, limit, connection_type: type }),
      // Resolve a saved id back to its label by id alone — independent of the type scope, so an
      // existing selection still renders even once the scope no longer admits it.
      resolve: async (key) => {
        const { items } = await searchConnections({ connection_id: Number(key), limit: 1 });
        return items[0] ?? null;
      },
      // Shown while `resolve` is in flight so a key-only value reads as selected rather than blank.
      interimValue: (key) => ({ connection_id: Number(key), connection_name: `Connection #${key}`, connection_type: '' }),
    }),
    [type]
  );

  return (
    <EditReference<ConnectionReference>
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      source={source}
      getKey={getKey}
      getLabel={getLabel}
      mandatory={mandatory}
      disabled={disabled}
      renderMenuItemChildren={(c, query) => (
        <div className="d-flex justify-content-between gap-3">
          <span>
            <Highlighter search={query}>{c.connection_name}</Highlighter>
          </span>
          <span className="text-body-secondary">{c.connection_type}</span>
        </div>
      )}
    />
  );
};
